import { useAuth } from "@workos-inc/authkit-react";
import { useEffect } from "react";
import { ROUTES } from "@/constants/routeConstants";
import { useAuthStore } from "@/store/auth";
import { useOrganizationStore } from "@/store/organization";

/**
 * Keeps ordinary switches on their current page, but sends organization
 * creation callbacks directly to the first usable dashboard route.
 */
function getOrganizationSwitchReturnTo(): string {
  const { pathname, search, hash } = window.location;
  if (pathname === ROUTES.ACCOUNT_NUX || pathname === ROUTES.ONBOARDING) {
    return ROUTES.ANALYTICS;
  }
  return `${pathname}${search}${hash}`;
}

/** Bridges AuthKit session state and organization switching into app stores. */
export const WorkosAuthLoader = () => {
  const {
    isLoading,
    user,
    signOut,
    getAccessToken,
    switchToOrganization,
    organizationId,
  } = useAuth();
  const setAuthState = useAuthStore((s) => s.setAuthState);
  const setOrganizationState = useOrganizationStore(
    (s) => s.setOrganizationState,
  );

  useEffect(() => {
    setAuthState({
      accessTokenGetter: getAccessToken,
      isLoadingExternalAuth: isLoading,
      hasExternalAuth: !!user,
      signOut: (options) =>
        signOut({
          returnTo: options?.redirectUrl ?? window.location.origin,
        }),
    });
  }, [setAuthState, isLoading, user, signOut, getAccessToken]);

  useEffect(() => {
    if (switchToOrganization) {
      setOrganizationState({
        setActiveExternalOrganization: (orgId) =>
          switchToOrganization({
            organizationId: orgId,
            signInOpts: {
              state: { returnTo: getOrganizationSwitchReturnTo() },
            },
          }),
      });
    }
  }, [switchToOrganization, setOrganizationState]);

  useEffect(() => {
    setOrganizationState({
      externalOrganizationId: organizationId ?? undefined,
    });
  }, [organizationId, setOrganizationState]);

  return null;
};
